import { API_CONFIG, ENDPOINTS } from '../config/apiConfig';

// Network connectivity test
export async function testNetworkConnectivity(): Promise<{ success: boolean; message: string; details?: any }> {
  try {
    // Test basic network connectivity
    if (navigator.onLine === false) {
      return { success: false, message: 'No internet connection detected' };
    }
    
    // Test a simple request to a reliable endpoint
    const testUrl = process.env.NODE_ENV === 'development' 
      ? '/api/test'  // Use local proxy test endpoint
      : 'https://httpbin.org/json'; // Simple test endpoint for production
      
    const response = await fetch(testUrl, {
      method: 'GET',
      signal: AbortSignal.timeout(5000) // 5 second timeout
    });
    
    if (response.ok) {
      return { 
        success: true, 
        message: 'Network connectivity test passed',
        details: { status: response.status, url: testUrl }
      };
    } else {
      return { 
        success: false, 
        message: `Network test failed with status ${response.status}`,
        details: { status: response.status, url: testUrl }
      };
    }
  } catch (error) {
    return { 
      success: false, 
      message: `Network connectivity test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      details: error
    };
  }
}

// Generic API request function
export async function makeAPIRequest(
  service: 'pehchanpe',
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: any;
    isFormData?: boolean;
    customHeaders?: Record<string, string>;
  } = {}
) {
  const { method = 'GET', data, isFormData = false, customHeaders = {} } = options;
  const config = API_CONFIG[service];
  
  // Use proxy URL for development, direct URL for production
  const baseURL = process.env.NODE_ENV === 'development' ? config.proxyURL : config.baseURL;
  const url = `${baseURL}${endpoint}`;
  
  // Debug logging to trace routing
  console.log('🔍 API Routing Debug:', {
    service,
    endpoint,
    baseURL,
    finalUrl: url,
    environment: process.env.NODE_ENV
  });
  
  const headers: Record<string, string> = {
    ...config.headers,
    ...customHeaders
  };
  
  // Add Content-Type for JSON requests if not already set and not FormData
  if (!isFormData && data && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Remove Content-Type for FormData (browser sets it automatically with boundary)
  if (isFormData && headers['Content-Type']) {
    delete headers['Content-Type'];
  }
  
  const requestOptions: RequestInit = {
    method,
    headers,
    signal: AbortSignal.timeout(config.timeout)
  };
  
  if (data) {
    if (isFormData) {
      requestOptions.body = data; // FormData object
    } else {
      requestOptions.body = JSON.stringify(data);
    }
  }
  
  try {
    console.log('Making API request:', {
      service,
      endpoint,
      baseURL,
      finalUrl: url,
      method,
      headers,
      ...(data && { body: isFormData ? 'FormData' : JSON.stringify(data) })
    });
    
    // Test network connectivity first
    if (navigator.onLine === false) {
      throw new Error('No internet connection detected. Please check your network connection and try again.');
    }
    
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorText
      });
      
      // Try to parse structured error response
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        // If parsing fails, use raw text
        throw new Error(`API Error (${response.status}): ${errorText}`);
      }
      
      // Format structured error message
      const error = new Error();
      error.name = 'APIError';
      (error as any).status = response.status;
      (error as any).data = errorData;
      
      // Create user-friendly error message
      let userMessage = `API Error (${response.status})`;
      
      if (errorData.message) {
        userMessage += `: ${errorData.message}`;
      } else if (errorData.error) {
        userMessage += `: ${errorData.error}`;
      }
      
      if (errorData.details?.message) {
        userMessage += `\n\nDetails: ${errorData.details.message}`;
      }
      
      if (errorData.details?.data) {
        const details = errorData.details.data;
        if (details.credit_score !== undefined) {
          userMessage += `\n\nProcessed Data:`;
          userMessage += `\n• Name: ${details.name || 'N/A'}`;
          userMessage += `\n• PAN: ${details.pan || 'N/A'}`;
          userMessage += `\n• Mobile: ${details.mobile || 'N/A'}`;
          userMessage += `\n• Gender: ${details.gender || 'N/A'}`;
          userMessage += `\n• Credit Score: ${details.credit_score || 'Not available'}`;
        }
      }
      
      if (errorData.message_code) {
        userMessage += `\n\nError Code: ${errorData.message_code}`;
      }
      
      error.message = userMessage;
      throw error;
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'TimeoutError') {
        throw new Error(`Request timeout after ${config.timeout}ms`);
      }
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        // Enhanced network error diagnosis
        const networkErrors = [
          'Network connection failed. This could be due to:',
          '• No internet connection',
          '• Server is not responding',
          '• CORS (Cross-Origin) policy blocking the request',
          '• Firewall or proxy blocking the connection',
          '• Invalid API endpoint URL',
          '',
          `Attempted URL: ${url}`,
          `Environment: ${process.env.NODE_ENV}`,
          `Base URL: ${baseURL}`,
          '',
          'Troubleshooting steps:',
          '1. Check your internet connection',
          '2. Verify the API server is running',
          '3. Check browser console for CORS errors',
          '4. Verify proxy configuration in development'
        ].join('\n');
        
        throw new Error(networkErrors);
      }
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}

// PehchanPe API Functions
export const pehchanpeAPI = {
  // Bank Statement Analysis - Step 1: Parse PDF Files
  parsePDF: async (data: { 
    files: File[]; 
    bankname: string; 
    accountType: 'Savings' | 'Current' | 'CC'; 
    password: string; 
    names: string;
  }) => {
    const formData = new FormData();
    
    // Append multiple files
    data.files.forEach((file) => {
      formData.append('files', file);
    });
    
    formData.append('bankname', data.bankname);
    formData.append('accountType', data.accountType);
    formData.append('Password', data.password || '');
    formData.append('names', data.names || '--');
    
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.parsePDF, {
      method: 'POST',
      data: formData,
      isFormData: true
    });
  },

  // Bank Statement Analysis - Step 2: Poll for completion using bad_id
  pollForAccountByBadId: async (
    badId: string, 
    onProgress?: (progress: number, status: string, attempt: number) => void
  ): Promise<string> => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    const delay = 10000; // 10 seconds
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Notify progress - checking attempt
        onProgress?.(
          Math.min((attempt / maxAttempts) * 90, 90), // Cap at 90% during polling
          `Checking processing status... (Attempt ${attempt + 1}/${maxAttempts})`,
          attempt + 1
        );

        // Add cache-busting query parameter
        const cacheBuster = `?_t=${Date.now()}&_attempt=${attempt + 1}`;
        const accounts = await makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.getAllBankAccounts + cacheBuster, {
          method: 'GET'
        });
        
        // Look for account with matching bad_id and completed processing
        const matchingAccount = accounts.find((account: any) => 
          account.bad_id === badId
        );
        
        if (matchingAccount) {
          console.log(`Found account with bad_id ${badId}:`, matchingAccount);
          
          // Update progress with actual processing progress
          const processingProgress = matchingAccount.progress || 0;
          onProgress?.(
            processingProgress,
            `Processing bank statement... ${processingProgress}% complete`,
            attempt + 1
          );
          
          // Check if processing is complete (progress = 100)
          if (matchingAccount.progress === 100) {
            console.log(`Processing complete for bad_id ${badId}`);
            onProgress?.(100, 'Processing complete! Generating analysis...', attempt + 1);
            // Return bad_id as the account identifier since there's no separate id field
            return matchingAccount.bad_id.toString();
          } else {
            console.log(`Processing still in progress: ${matchingAccount.progress}% complete`);
          }
        } else {
          // Account not found yet
          onProgress?.(
            Math.min((attempt / maxAttempts) * 50, 50), // Max 50% if account not found
            `Waiting for processing to start... (${attempt + 1}/${maxAttempts})`,
            attempt + 1
          );
        }
        
        console.log(`Polling attempt ${attempt + 1}/${maxAttempts} for bad_id ${badId}...`);
        
        // Wait before next attempt (except on last attempt)
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        console.error(`Polling attempt ${attempt + 1} failed:`, error);
        onProgress?.(
          Math.min((attempt / maxAttempts) * 90, 90),
          `Connection error, retrying... (${attempt + 1}/${maxAttempts})`,
          attempt + 1
        );
        if (attempt === maxAttempts - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw new Error('Processing timeout: Bank statement is taking longer than expected to process. Please try again later.');
  },

  // Bank Statement Analysis - Step 2: Poll for completion using timestamp
  pollForAccountsByTimestamp: async (
    timestamp: number,
    onProgress?: (progress: number, status: string, attempt: number) => void
  ): Promise<string> => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    const delay = 10000; // 10 seconds
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Notify progress - checking attempt
        onProgress?.(
          Math.min((attempt / maxAttempts) * 90, 90), // Cap at 90% during polling
          `Searching for processed accounts... (Attempt ${attempt + 1}/${maxAttempts})`,
          attempt + 1
        );

        // Add cache-busting query parameter
        const cacheBuster = `?_t=${Date.now()}&_attempt=${attempt + 1}`;
        const accounts = await makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.getAllBankAccounts + cacheBuster, {
          method: 'GET'
        });
        
        // Look for accounts created after the upload timestamp
        const recentAccounts = accounts.filter((account: any) => {
          const accountTimestamp = new Date(account.createdat || account.created_at || account.timestamp || account.created).getTime();
          return accountTimestamp >= timestamp;
        });
        
        if (recentAccounts.length > 0) {
          // Find accounts with progress information
          const accountsWithProgress = recentAccounts.filter((account: any) => account.progress !== undefined);
          
          if (accountsWithProgress.length > 0) {
            // Get the latest account and its progress
            const latestAccount = accountsWithProgress
              .sort((a: any, b: any) => 
                new Date(b.createdat || b.created_at || b.timestamp || b.created).getTime() - 
                new Date(a.createdat || a.created_at || a.timestamp || a.created).getTime()
              )[0];
              
            const processingProgress = latestAccount.progress || 0;
            onProgress?.(
              processingProgress,
              `Processing bank statement... ${processingProgress}% complete`,
              attempt + 1
            );
            
            // Check if processing is complete
            if (latestAccount.progress === 100) {
              console.log(`Found recent completed account: ${latestAccount.bad_id}`);
              onProgress?.(100, 'Processing complete! Generating analysis...', attempt + 1);
              return latestAccount.bad_id.toString();
            }
          } else {
            onProgress?.(
              Math.min((attempt / maxAttempts) * 50, 50),
              `Found ${recentAccounts.length} recent account(s), waiting for processing to start...`,
              attempt + 1
            );
            console.log(`Found ${recentAccounts.length} recent account(s), but none are fully processed yet`);
          }
        } else {
          onProgress?.(
            Math.min((attempt / maxAttempts) * 30, 30),
            `Waiting for upload to be recognized... (${attempt + 1}/${maxAttempts})`,
            attempt + 1
          );
        }
        
        console.log(`Polling attempt ${attempt + 1}/${maxAttempts} for timestamp ${new Date(timestamp).toISOString()}...`);
        
        // Wait before next attempt (except on last attempt)
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        console.error(`Polling attempt ${attempt + 1} failed:`, error);
        onProgress?.(
          Math.min((attempt / maxAttempts) * 90, 90),
          `Connection error, retrying... (${attempt + 1}/${maxAttempts})`,
          attempt + 1
        );
        if (attempt === maxAttempts - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw new Error('Processing timeout: Bank statement is taking longer than expected to process. Please try again later.');
  },
  
  // Get All Bank Accounts
  getAllBankAccounts: async () => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.getAllBankAccounts);
  },
  
  // Check analysis status for specific bad_id (single API call, no polling)
  checkAnalysisStatusByBadId: async (badId: string) => {
    try {
      const accounts = await makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.getAllBankAccounts);
      
      console.log('Retrieved accounts from API:', accounts?.length || 0, 'accounts');
      console.log('Looking for bad_id:', badId, 'type:', typeof badId);
      
      // Debug: Check first few accounts for bad_id types
      if (accounts && accounts.length > 0) {
        console.log('Sample account bad_ids:', accounts.slice(0, 3).map((acc: any) => ({ 
          bad_id: acc.bad_id, 
          type: typeof acc.bad_id 
        })));
      }
      
      // Find account with matching bad_id (handle both string and number types)
      const matchingAccount = accounts.find((account: any) => {
        const accountBadId = account.bad_id;
        const searchBadId = badId;
        
        // Convert both to strings for comparison
        return String(accountBadId) === String(searchBadId);
      });
      
      if (!matchingAccount) {
        console.log('Available bad_ids:', accounts.map((acc: any) => acc.bad_id));
        
        // Check if the bad_id exists in the array with different comparison
        const badIdExists = accounts.some((acc: any) => String(acc.bad_id) === String(badId));
        console.log(`bad_id ${badId} exists in array:`, badIdExists);
        
        // Instead of throwing an error, return a status indicating the analysis is still being processed
        // This allows the user to use the "Check Status" button to wait for the analysis to appear
        return {
          account: null,
          isComplete: false,
          progress: 0,
          status: 'processing', // Indicates the analysis is still being processed
          message: 'Analysis is still being processed. Please wait a few minutes and try again.'
        };
      }
      
      console.log('Found matching account:', {
        bad_id: matchingAccount.bad_id,
        progress: matchingAccount.progress,
        status: matchingAccount.status
      });
      
      return {
        account: matchingAccount,
        isComplete: matchingAccount.progress === 100,
        progress: matchingAccount.progress || 0,
        status: matchingAccount.status || 'processing'
      };
    } catch (error: any) {
      // Re-throw with better context for 500 errors
      if (error.status === 500) {
        throw new Error('External API error: Bank statement service is temporarily unavailable');
      }
      throw error;
    }
  },
  
  // Bank Statement Analysis - Step 3: Get Analysis Data
  getBSAAnalysis: async (accountId: string, startDate: string, endDate: string) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.analysis, {
      method: 'POST',
      data: {
        accountIds: accountId, // Use accountIds (plural) as string
        startDate,
        endDate
      }
    });
  },

  // Bank Statement Analysis - Step 4: Get Transactions Data
  getBSATransactions: async (accountId: string, startDate: string, endDate: string) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.transactions, {
      method: 'POST',
      data: {
        accountIds: accountId, // Use accountIds (plural) as string
        startDate,
        endDate
      }
    });
  },
  
  // Get Credit Report
  getCreditReport: async (data: {
    name: string;
    pan: string;
    gender: string;
    mobile: string;
    consent: string;
  }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.creditReport, {
      method: 'POST',
      data
    });
  }
};

// PehchanPe eKYC API Functions (Unified Endpoint)
export const pehchanpeEkycAPI = {
  // Driving License Verification
  verifyDrivingLicense: async (data: { id_number: string; dob: string }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.ekyc, {
      method: 'POST',
      data: {
        verificationType: 'driving-license',
        id_number: data.id_number,
        dob: data.dob
      }
    });
  },
  
  // FSSAI License Verification
  verifyFSSAI: async (data: { id_number: string }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.ekyc, {
      method: 'POST',
      data: {
        verificationType: 'fssai',
        id_number: data.id_number
      }
    });
  },
  
  // Drug License Verification
  verifyDrugLicense: async (data: { state_code: string; license_no: string }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.ekyc, {
      method: 'POST',
      data: {
        verificationType: 'drug-license',
        state_code: data.state_code,
        license_no: data.license_no
      }
    });
  },
  
  // Voter ID Verification
  verifyVoterID: async (data: { id_number: string; state_code: string }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.ekyc, {
      method: 'POST',
      data: {
        verificationType: 'voter-id',
        id_number: data.id_number,
        state_code: data.state_code
      }
    });
  },
  
  // PAN Details Verification
  getPANDetails: async (data: { panNumber: string }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.ekyc, {
      method: 'POST',
      data: {
        verificationType: 'pan-details',
        panNumber: data.panNumber
      }
    });
  },
  
  // CA Verification
  verifyCA: async (data: { panNumber: string }) => {
    return makeAPIRequest('pehchanpe', ENDPOINTS.pehchanpe.ekyc, {
      method: 'POST',
      data: {
        verificationType: 'ca-verification',
        panNumber: data.panNumber
      }
    });
  }
};

// Utility functions
export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
}

export function formatDateDDMMYYYY(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`; // DD/MM/YYYY format
}

export function validatePAN(pan: string): boolean {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
}



export function validateMobile(mobile: string): boolean {
  const mobileRegex = /^[6-9]\d{9}$/;
  return mobileRegex.test(mobile);
}
